import '../styles/PreviewContainer.scss';

import { DevicesIcon } from '@phosphor-icons/react';
import { PhoneFrame } from '@whistler-ui/k2/components/PhoneFrame';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import WebSheetTemplateWrapper from '@whistler-ui/k2/components/WebSheetTemplates/WebSheetTemplateWrapper';
import { DEVICE_TYPES } from '@whistler-ui/k2/constants';
import { DeviceSdk, TemplateType, WebSheetTag } from '@whistler-ui/k2/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PREVIEW_WEB_SHEET, PREVIEW_WEBSHEET_TEXT } from '../../../constants';
import { useWebsheetConfigStore } from '../../../store';
import { BrandResponse } from '../../../types';
import { convertStructure } from '../helpers/brandingTransformers';
import {
  getDefaultProxyFunction,
  getServiceProxyFunction,
} from '../helpers/proxyFunctions';

interface Props {
  brand: BrandResponse;
  tag: WebSheetTag;
  templateType: TemplateType;
  loading?: boolean;
  settings: any;
  deviceType: (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];
  emptyUseCaseSettings?: boolean;
}

export const PreviewContainer = ({
  brand,
  tag,
  templateType,
  loading,
  settings,
  deviceType,
  emptyUseCaseSettings,
}: Props) => {
  const { t } = useTranslation();
  const {
    values: { currentPreviewType, selectedField, preview },
  } = useWebsheetConfigStore();

  const currentText = useMemo(() => {
    if (preview?.localisationSettings) {
      return preview?.localisationSettings;
    }
    return PREVIEW_WEBSHEET_TEXT;
  }, [preview, settings, brand.id]);

  const currentConfigurationSettings = useMemo(() => {
    if (preview?.useCaseSettings?.configuration_settings) {
      return preview.useCaseSettings.configuration_settings;
    }
    return {};
  }, [preview, tag, brand.id, settings]);

  const currentSettings = useMemo(() => {
    if (settings?.useCaseSettings?.configuration_settings) {
      return settings?.useCaseSettings?.configuration_settings;
    }

    return PREVIEW_WEB_SHEET.settings;
  }, [settings, tag, brand.id]);

  const styles = {
    ...convertStructure(brand),
    ...brand,
    ...(currentSettings as Record<string, unknown>),
    ...(currentConfigurationSettings as Record<string, unknown>),
    // k2 templates read the flags NESTED (useGetUseCaseSettings →
    // values.configuration_settings.*); the flat spreads above alone left
    // e.g. emergency_address_autocomplete_enabled invisible to the preview.
    configuration_settings: {
      ...(currentSettings as Record<string, unknown>),
      ...(currentConfigurationSettings as Record<string, unknown>),
    },
  };

  const services = getServiceProxyFunction(
    PREVIEW_WEB_SHEET.settings,
    currentSettings as Record<string, unknown>,
    currentConfigurationSettings as Record<string, unknown>,
  );

  return (
    <PhoneFrame type="dark" loading={loading} variant={deviceType}>
      {emptyUseCaseSettings ? (
        <div className="bg-white h-full flex items-center justify-center px-4">
          <Stack direction="column" gap={3}>
            <div className="flex justify-center">
              <DevicesIcon size={64} className="text-gray-400" />
            </div>
            <Heading className="text-gray-800! text-center!">
              Preview Unavailable
            </Heading>
            <Paragraph className="text-center!">
              Save settings and continue to next steps to generate device
              preview.
            </Paragraph>
          </Stack>
        </div>
      ) : (
        <WebSheetTemplateWrapper
          services={services}
          deviceSdk={getDefaultProxyFunction() as DeviceSdk}
          currentLocale="en-us"
          webSheet={{}}
          values={{}}
          templateType={templateType}
          selectedField={selectedField}
          currentPreviewType={currentPreviewType}
          text={currentText}
          isPortalPreview
          deviceType={deviceType}
          styles={styles}
          t={t}
        />
      )}
    </PhoneFrame>
  );
};
